import { getAuthHeaders } from "./runtime";
import type { Capability, ModelItem } from "./catalog";

function getApiBase(): string {
  return (
    import.meta.env.VITE_API_BASE_URL ||
    (typeof window !== "undefined" && window.location.hostname === "app.znich.vladkas.com"
      ? "https://znich.vladkas.com"
      : "")
  );
}

export function apiUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${getApiBase()}${path}`;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly retryable: boolean;
  readonly retryAfter?: number;
  readonly requestId?: string;

  constructor(
    message: string,
    options: { status?: number; code?: string; retryable?: boolean; retryAfter?: number; requestId?: string } = {}
  ) {
    super(message);
    this.name = "ApiError";
    this.status = options.status ?? 0;
    this.code = options.code;
    this.retryable = options.retryable ?? (this.status === 0 || this.status >= 500 || this.status === 429);
    this.retryAfter = options.retryAfter;
    this.requestId = options.requestId;
  }
}

function responseMessage(payload: unknown, status: number): { message: string; code?: string; retryable?: boolean } {
  const data = payload && typeof payload === "object" ? payload as Record<string, unknown> : {};
  const detail = typeof data.detail === "string" ? data.detail : undefined;
  const error = typeof data.error === "string" ? data.error : undefined;
  const code = typeof data.code === "string" ? data.code : undefined;
  const retryable = typeof data.retryable === "boolean" ? data.retryable : undefined;
  const fallback = status === 401 || status === 403
    ? "Сессия устарела. Откройте приложение из Telegram ещё раз."
    : status === 402
      ? "Недостаточно токенов для запуска этой задачи."
      : status === 429
        ? "Сервис занят. Попробуйте ещё раз через минуту."
        : status >= 500
          ? "Сервис временно недоступен. Попробуйте ещё раз позже."
          : "Не удалось выполнить запрос. Проверьте данные и попробуйте ещё раз.";
  return { message: detail || error || fallback, code, retryable };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  Object.entries(getAuthHeaders()).forEach(([key, value]) => headers.set(key, String(value)));
  if (!(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  let response: Response;
  const controller = new AbortController();
  let timedOut = false;
  const timeout = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, 30_000);
  const abortFromCaller = () => controller.abort();
  if (init.signal) {
    if (init.signal.aborted) controller.abort();
    else init.signal.addEventListener("abort", abortFromCaller, { once: true });
  }
  try {
    response = await fetch(apiUrl(path) || path, { ...init, headers, signal: controller.signal });
  } catch (error) {
    if (timedOut) {
      throw new ApiError("Сервис отвечает слишком долго. Проверьте результат через несколько секунд.", {
        retryable: true,
      });
    }
    if (controller.signal.aborted && init.signal?.aborted) {
      throw new ApiError("Запрос отменён.", { retryable: false });
    }
    throw new ApiError("Не удалось связаться с Znich. Проверьте интернет-соединение и попробуйте ещё раз.", {
      retryable: true,
    });
  } finally {
    window.clearTimeout(timeout);
    init.signal?.removeEventListener("abort", abortFromCaller);
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const parsed = responseMessage(payload, response.status);
    const retryAfterRaw = response.headers.get("Retry-After");
    const retryAfter = retryAfterRaw && /^\d+$/.test(retryAfterRaw) ? Number(retryAfterRaw) : undefined;
    throw new ApiError(parsed.message, {
      status: response.status,
      code: parsed.code,
      retryable: parsed.retryable,
      retryAfter,
      requestId: response.headers.get("X-Request-ID") || undefined
    });
  }
  return response.json() as Promise<T>;
}

export type DraftPayload = {
  source_interface: "telegram_mini_app" | "standalone_web";
  capability: Capability;
  prompt?: string;
  params?: Record<string, unknown>;
  selected_model_id?: string | null;
};

export type DraftUpdatePayload = {
  prompt?: string;
  params?: Record<string, unknown>;
  selected_model_id?: string | null;
  model_selection_mode?: "auto" | "manual";
  preset_id?: string | null;
};

export type Draft = {
  id: string;
  capability: Capability;
  prompt: string | null;
  selected_model_id: string | null;
  model_selection_mode: "auto" | "manual";
  params: Record<string, unknown>;
  estimated_cost: string | null;
  status: string;
};

export type Task = {
  id: string;
  draft_id?: string | null;
  capability: string;
  status: string;
  model_id: string;
  /** Stable user-facing name; model_id remains an internal audit field. */
  model_name?: string | null;
  prompt: string;
  params?: Record<string, unknown>;
  aspect_ratio?: string | null;
  estimated_tokens: string;
  actual_tokens?: string | null;
  error_message?: string | null;
  created_at?: string | null;
  assets?: Array<{
    id: string;
    task_id: string;
    type: string;
    preview_url?: string | null;
    download_url?: string | null;
    created_at?: string | null;
    metadata?: Record<string, any> | null;
  }>;
};

export type Transaction = {
  id: string;
  task_id?: string | null;
  transaction_type: string;
  amount: string;
  amount_formatted?: string | null;  // Server-formatted with proper plural, e.g. "5 токенов"
  balance_before: string;
  balance_after: string;
  provider_cost_usd?: string | null;  // Actual OpenRouter cost in USD
  markup_multiplier?: string | null;  // Applied markup (for audit trail)
  description?: string | null;
  created_at?: string | null;
};

export type CapabilityVisibility = {
  capability: string;
  title: string;
  bot: string;
  webapp: string;
  hybrid: string;
};

export type TelegramSession = {
  account: {
    id: string;
    language_code: string;
    token_balance: string | null;
    telegram_username?: string | null;
    telegram_full_name?: string | null;
  };
  auth: {
    mode: string;
    status: string;
  };
  balance?: {
    token_balance: string | null;
    preferred_currency: string;
  };
};

export async function fetchModels(capability?: Capability | null, search?: string, provider?: string): Promise<ModelItem[]> {
  let url = "/api/models?limit=1000";
  if (capability) {
    url += `&capability=${encodeURIComponent(capability)}`;
  }
  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }
  if (provider) {
    url += `&provider=${encodeURIComponent(provider)}`;
  }
  const result = await request<{ items: ModelItem[] }>(url);
  return result.items;
}

export async function fetchCapabilities(): Promise<CapabilityVisibility[]> {
  const result = await request<{ items?: Array<CapabilityVisibility | null> } | CapabilityVisibility[]>("/api/capabilities");
  const items = Array.isArray(result) ? result : result && Array.isArray(result.items) ? result.items : [];
  return items.filter((item): item is CapabilityVisibility => Boolean(item && typeof item === "object"));
}

export async function initTelegramSession(initData: string): Promise<TelegramSession> {
  return request<TelegramSession>("/api/telegram/init", {
    method: "POST",
    body: JSON.stringify({ init_data: initData })
  });
}

export async function fetchCurrentAccount(): Promise<{ account: TelegramSession["account"] }> {
  return request("/api/account");
}

export async function createDraft(payload: DraftPayload): Promise<Draft> {
  return request<Draft>("/api/drafts", { method: "POST", body: JSON.stringify(payload) });
}

export async function fetchDraft(draftId: string): Promise<Draft> {
  return request<Draft>(`/api/drafts/${encodeURIComponent(draftId)}`);
}

export async function updateDraft(draftId: string, payload: DraftUpdatePayload): Promise<Draft> {
  return request<Draft>(`/api/drafts/${draftId}`, { method: "PATCH", body: JSON.stringify(payload) });
}

export type EstimateResponse = {
  draft: Draft;
  estimated_cost: string;
  estimated_cost_formatted: string;
  confidence: "exact" | "approximate" | "unavailable";
  breakdown: Record<string, any>;
  warnings: string[];
  model: ModelItem | null;
};

export async function estimateDraft(draftId: string): Promise<EstimateResponse> {
  return request(`/api/drafts/${draftId}/estimate`, { method: "POST" });
}

export async function generateDraft(draftId: string): Promise<Task> {
  return request(`/api/drafts/${draftId}/generate`, { method: "POST", body: JSON.stringify({ execute: true }) });
}

export async function uploadAttachment(draftId: string | null, file: File, source: string): Promise<{ id: string; mime_type: string; size: number }> {
  const form = new FormData();
  form.set("file", file);
  if (draftId) form.set("draft_id", draftId);
  form.set("source", source);
  return request("/api/attachments", { method: "POST", body: form });
}

export async function fetchTasks(): Promise<Task[]> {
  const result = await request<{ items?: Array<Task | null> } | Task[]>("/api/tasks?limit=24");
  const items = Array.isArray(result) ? result : result && Array.isArray(result.items) ? result.items : [];
  return items.filter((item): item is Task => Boolean(item && typeof item === "object"));
}

export async function fetchTask(taskId: string): Promise<Task> {
  return request<Task>(`/api/tasks/${encodeURIComponent(taskId)}`);
}

export async function createReferenceDraftFromTask(
  taskId: string
): Promise<{ draft: Draft; attachment: { id: string; mime_type: string; size: number }; preview_url?: string | null }> {
  return request(`/api/tasks/${encodeURIComponent(taskId)}/reference`, { method: "POST" });
}

export async function fetchBalance(): Promise<{ token_balance: string; preferred_currency: string; top_up_available: boolean }> {
  return request("/api/balance");
}

export async function fetchTransactions(): Promise<Transaction[]> {
  const result = await request<{ items?: Array<Transaction | null> } | Transaction[]>("/api/transactions?limit=20");
  const items = Array.isArray(result) ? result : result && Array.isArray(result.items) ? result.items : [];
  return items.filter((item): item is Transaction => Boolean(item && typeof item === "object"));
}

export async function deleteTask(taskId: string): Promise<void> {
  return request<void>(`/api/tasks/${taskId}`, { method: "DELETE" });
}

export type ResultAction = {
  action: string;
  title: string;
  creates_draft: boolean;
  target_capability: string | null;
};

export async function fetchTaskActions(taskId: string): Promise<{ task_id: string; result_type: string; actions: ResultAction[] }> {
  return request(`/api/tasks/${taskId}/actions`);
}

export async function executeResultAction(taskId: string, action: string): Promise<{ ok: boolean; action: string; draft?: Draft; preview_url?: string | null }> {
  return request(`/api/tasks/${taskId}/actions/${action}`, { method: "POST" });
}

export async function promptBuilderGenerate(prompt: string, targetCapability: string): Promise<{ task: Task; assets: Array<{ id: string; type: string; metadata?: Record<string, any> }>; target_capability: string }> {
  const form = new FormData();
  form.set("prompt", prompt);
  form.set("target_capability", targetCapability);
  return request("/api/prompt-builder", { method: "POST", body: form });
}

export async function fetchCatalogHealth(): Promise<{ status: string; total_models: number; available_models: number; providers: string[]; last_seen_at?: string | null }> {
  return request("/api/catalog/health");
}

export type ModelsStats = {
  total_models: number;
  total_providers: number;
  models_by_provider: Record<string, number>;
  models_by_output_modality: Record<string, number>;
  models_by_input_modality: Record<string, number>;
  models_by_capability: Record<string, number>;
  last_sync_at?: string | null;
  last_sync_status?: string | null;
  last_sync_error_safe?: string | null;
};

export async function fetchModelsStats(): Promise<ModelsStats> {
  return request<ModelsStats>("/api/models/stats");
}
